const getInitialState = () => ({
  GPS_OPTIONS: {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 9000,
  },

  GPS_SUPPORTED: "geolocation" in navigator,
  GPS_PERMISSION: null, // 'granted', 'denied', 'prompt', or null if unknown

  GPS_POSITION: null,
  GPS_ERROR: null,
  GPS_WATCH_ID: null,
  GPS_IS_LOADING: false,
});

const state = getInitialState();

const getters = {
  GPS_IS_WATCHING: (state) => state.GPS_WATCH_ID !== null,
  GPS_LNGLAT: (state) =>
    state.GPS_POSITION
      ? {
          lng: state.GPS_POSITION.coords.longitude,
          lat: state.GPS_POSITION.coords.latitude,
        }
      : null,
};

const mutations = {
  GPS_SET_PERMISSION(state, status) {
    state.GPS_PERMISSION = status;
  },
  GPS_SET_LOADING(state, isLoading) {
    state.GPS_IS_LOADING = true;
  },
  GPS_SET_POSITION(state, position) {
    state.GPS_POSITION = position;
    state.GPS_ERROR = null;
    state.GPS_IS_LOADING = false;
  },
  GPS_SET_ERROR(state, error) {
    state.GPS_ERROR = error;
    state.GPS_POSITION = null;
    state.GPS_IS_LOADING = false;
  },
  GPS_SET_WATCH_ID(state, id) {
    state.GPS_WATCH_ID = id;
    state.GPS_IS_LOADING = false;
  },
  GPS_CLEAR_WATCH_ID(state) {
    state.GPS_WATCH_ID = null;
    state.GPS_IS_LOADING = false;
  },
};

const actions = {
  async GPS_CHECK_PERMISSION({ commit, state }) {
    // function is purely for troubleshooting, not really used
    if (!state.GPS_SUPPORTED || !navigator.permissions) {
      commit("GPS_SET_PERMISSION", null);
      return;
    }
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      commit("GPS_SET_PERMISSION", status.state);
      status.onchange = () => {
        commit("GPS_SET_PERMISSION", status.state);
      };
    } catch (e) {
      commit("GPS_SET_PERMISSION", null);
    }
  },
  async GPS_GET_CURRENT_POSITION({ commit, dispatch }) {
    if (!("geolocation" in navigator)) {
      commit("GPS_SET_ERROR", { message: "Geolocation not supported" });
      return Promise.reject({ message: "Geolocation not supported" });
    }
    commit("GPS_SET_LOADING");
    dispatch("GPS_CHECK_PERMISSION");
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // paramaters from:
          // https://developer.mozilla.org/en-US/docs/Web/API/GeolocationCoordinates

          const position = {
            lngLat: {
              lng: pos.coords.longitude,
              lat: pos.coords.latitude,
            },
            accuracy: pos.coords.accuracy, // expressed in meters.
            heading: pos.coords.heading, // in degrees
            zoom: _accuracyToZoom(pos.coords.accuracy),
          };
          commit("GPS_SET_POSITION", position);
          resolve(position); // <-- Return the position
        },
        (err) => {
          commit("GPS_SET_ERROR", err);
          reject(err); // <-- Return the error
        }
      );
    });
  },
  async GPS_WATCH_POSITION({ commit, dispatch, state }) {
    if (!("geolocation" in navigator)) {
      commit("GPS_SET_ERROR", { message: "Geolocation not supported" });
      return Promise.reject({ message: "Geolocation not supported" });
    }
    dispatch("GPS_CHECK_PERMISSION");
    if (state.GPS_WATCH_ID !== null) {
      // Already watching, return current position as a resolved Promise
      return Promise.resolve(state.GPS_POSITION);
    }
    return new Promise((resolve, reject) => {
      const id = navigator.geolocation.watchPosition(
        (pos) => {
          commit("GPS_SET_POSITION", pos);
          resolve(pos); // Resolve on first update
        },
        (err) => {
          commit("GPS_SET_ERROR", err);
          reject(err);
        },
        state.GPS_OPTIONS
      );
      commit("GPS_SET_WATCH_ID", id);
    });
  },
  GPS_CLEAR_WATCH({ commit, state }) {
    if (state.GPS_WATCH_ID !== null && "geolocation" in navigator) {
      navigator.geolocation.clearWatch(state.GPS_WATCH_ID);
      commit("GPS_CLEAR_WATCH_ID");
    }
  },
};

function _accuracyToZoom(accuracyMeters) {
  if (!accuracyMeters || accuracyMeters <= 0) return 18;
  const zoom = 24 - Math.log2(accuracyMeters);
  return Math.max(0, Math.min(21, Math.round(zoom)));
}

export default {
  getInitialState,
  state,
  getters,
  mutations,
  actions,
};
